import { useCallback, useEffect, useState } from 'react'
import { LuChevronLeft, LuChevronRight } from 'react-icons/lu'
import {
  carouselAfrica,
  carouselBridge,
  carouselConsultation,
  carouselWorkshop,
  kandakePortrait,
} from '@/assets/siteMedia'

const SLIDES = [
  {
    numeral: 'I',
    image: kandakePortrait,
    alt: 'Kandake Makonnen smiling at a table with herbs and herbal medicine books',
    title: 'Instrumental Health – Changing the Mindset',
    position: 'object-[50%_35%]',
  },
  {
    numeral: 'II',
    image: carouselAfrica,
    alt: 'A woman pounding herbs with a wooden pestle in a village courtyard in Africa',
    title: 'Rooted in Ancestral Wisdom',
    position: 'object-[50%_50%]',
  },
  {
    numeral: 'III',
    image: carouselWorkshop,
    alt: 'Kandake teaching a practical herbal workshop to a round table of students',
    title: 'Knowledge That Empowers',
    position: 'object-[50%_50%]',
  },
  {
    numeral: 'IV',
    image: carouselConsultation,
    alt: 'A patient in conversation with Kandake during a one-to-one consultation',
    title: 'Your Health. Your Individual Journey.',
    position: 'object-[50%_50%]',
  },
  {
    numeral: 'V',
    image: carouselBridge,
    alt: 'A bridge joining ancient healing traditions with a modern scientific city at sunrise',
    title: 'Bridging Indigenous Wisdom & Modern Science',
    position: 'object-[50%_50%]',
  },
] as const

const INTERVAL_MS = 6000

export const HeroCarousel = () => {
  const [index, setIndex] = useState(0)
  const [paused, setPaused] = useState(false)

  const go = useCallback((next: number) => setIndex((next + SLIDES.length) % SLIDES.length), [])

  useEffect(() => {
    if (paused || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
    const id = window.setTimeout(() => setIndex((i) => (i + 1) % SLIDES.length), INTERVAL_MS)
    return () => window.clearTimeout(id)
  }, [index, paused])

  const slide = SLIDES[index]

  return (
    <figure
      role="group"
      aria-roledescription="carousel"
      aria-label="Instrumental Health photo carousel"
      className="relative my-7 min-w-0 lg:col-start-2 lg:row-start-1 lg:row-span-5 lg:my-0"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
      onKeyDown={(e) => {
        if (e.key === 'ArrowLeft') go(index - 1)
        if (e.key === 'ArrowRight') go(index + 1)
      }}
    >
      <div className="overflow-hidden rounded-t-[2rem] rounded-b-2xl border border-white/10 bg-green-900 shadow-xl sm:rounded-t-[3rem] lg:rounded-t-[4rem]">
        <div className="relative aspect-[16/10] w-full lg:aspect-[16/9]">
          {SLIDES.map((s, i) => (
            <img
              key={s.numeral}
              src={s.image}
              alt={s.alt}
              aria-hidden={i !== index}
              loading={i === 0 ? 'eager' : 'lazy'}
              fetchPriority={i === 0 ? 'high' : 'auto'}
              className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-1000 ease-in-out motion-reduce:transition-none ${s.position} ${
                i === index ? 'opacity-100' : 'opacity-0'
              }`}
            />
          ))}

          <div className="absolute inset-x-3 top-1/2 flex -translate-y-1/2 justify-between">
            {[
              { label: 'Previous slide', Icon: LuChevronLeft, to: index - 1 },
              { label: 'Next slide', Icon: LuChevronRight, to: index + 1 },
            ].map(({ label, Icon, to }) => (
              <button
                key={label}
                type="button"
                aria-label={label}
                onClick={() => go(to)}
                className="flex h-10 w-10 items-center justify-center rounded-full bg-green-950/55 text-base-50 backdrop-blur transition-colors hover:bg-green-950/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-200"
              >
                <Icon aria-hidden="true" className="h-5 w-5" />
              </button>
            ))}
          </div>
        </div>

        <figcaption className="border-t border-white/10 px-5 py-4 sm:px-7 sm:py-5">
          <div aria-live={paused ? 'polite' : 'off'} className="flex items-start gap-4">
            <span aria-hidden="true" className="pt-1 font-display text-sm text-gold-200">
              {slide.numeral}
            </span>
            <p
              key={slide.numeral}
              className="min-h-[3.5rem] animate-[fadeIn_0.8s_ease-out] font-display text-lg leading-snug sm:text-xl"
            >
              {slide.title}
            </p>
          </div>
          <div className="mt-3 flex items-center gap-2">
            {SLIDES.map((s, i) => (
              <button
                key={s.numeral}
                type="button"
                aria-label={`Go to slide ${i + 1}: ${s.title}`}
                aria-current={i === index}
                onClick={() => go(i)}
                className="group flex h-6 items-center focus-visible:outline-none"
              >
                <span
                  className={`block h-1 rounded-full transition-all group-focus-visible:ring-2 group-focus-visible:ring-gold-200 ${
                    i === index ? 'w-8 bg-gold-200' : 'w-4 bg-white/30 group-hover:bg-white/60'
                  }`}
                />
              </button>
            ))}
          </div>
        </figcaption>
      </div>
    </figure>
  )
}
